// Admin editor for the blocked-access page copy (heading + body), shown to a
// visitor whose email is on the blacklist. Mirrors the confirmation-page
// fields: the admin sets free-form text, so any contact email goes in the body.
// Self-contained save flow so it can live on the Blacklist page, which is
// otherwise backed by the email-lists slice rather than admin-settings.
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, TextField, Button, Stack, CircularProgress, Alert,
} from '@mui/material';
import {
  fetchAdminSettings, saveAdminSettings, clearSaveError,
} from '../../../store/admin-settings-slice.js';
import { fetchPublicConfig } from '../../../store/settings-slice.js';
import { showSnackbar } from '../../../store/snackbar-slice.js';

const KEYS = ['blocked_page_title', 'blocked_page_body'];

const BlockedPageSection = () => {
  const dispatch = useDispatch();
  const { settings, saving, saveError } = useSelector((s) => s.adminSettings);
  const [form, setForm] = useState(null);

  useEffect(() => { dispatch(fetchAdminSettings()); }, [dispatch]);

  useEffect(() => {
    if (settings) {
      setForm({
        blocked_page_title: settings.blocked_page_title ?? '',
        blocked_page_body: settings.blocked_page_body ?? '',
      });
    }
  }, [settings]);

  if (!form) return null;

  const dirty = KEYS.some((k) => (form[k] ?? '').trim() !== (settings?.[k] ?? '').trim());

  const setField = (key, value) => {
    if (saveError) dispatch(clearSaveError());
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const payload = {
      blocked_page_title: form.blocked_page_title.trim(),
      blocked_page_body: form.blocked_page_body.trim(),
    };
    const result = await dispatch(saveAdminSettings(payload));
    if (saveAdminSettings.fulfilled.match(result)) {
      dispatch(fetchPublicConfig());
      dispatch(showSnackbar({ message: 'Blocked-access page saved.', severity: 'success' }));
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap',
      }}
      >
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            Blocked-access page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shown when a blocked visitor is denied access. Include any contact
            email directly in the body text.
          </Typography>
        </Box>
        <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </Box>

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      <Stack spacing={2}>
        <TextField
          label="Heading"
          value={form.blocked_page_title}
          onChange={(e) => setField('blocked_page_title', e.target.value)}
          fullWidth
        />
        <TextField
          label="Body text"
          value={form.blocked_page_body}
          onChange={(e) => setField('blocked_page_body', e.target.value)}
          multiline
          minRows={4}
          fullWidth
        />
      </Stack>
    </Paper>
  );
};

export default BlockedPageSection;
